import axios from 'axios';
import SearchMockData from './SearchMockData';

axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

function emptyPromise() {
  return new Promise(resolve => resolve());
}

function shouldUseMockData() {
  return (
    /\.netlify\.com$/i.test(window.location.host)
    || (window.localStorage
      && window.localStorage.getItem('searchUseMockData') === 'true')
  );
}

/**
 * Takes params to be appended to URL. Arrays will be joined with comma.
 * Multiple values for contentType, audience, nation and topics may be used, delimited by comma.
 * Default page size is determined server-side if not provided.
 * @returns {string} Search URL parameters.
 */
function BuildSearchUrlParameters(parameters) {
  const urlParameters = [];
  const parameterKeys = Object.keys(parameters);
  for (let i = 0; i < parameterKeys.length; i += 1) {
    const key = parameterKeys[i];
    const value = parameters[key];
    if (value !== undefined) {
      if (value instanceof Array) {
        if (value.length > 0) {
          const commaSeparatedArrayValues = value
            .map(item => encodeURIComponent(item))
            .join(',');
          urlParameters.push(`${key}=${commaSeparatedArrayValues}`);
        }
      } else if (
        (typeof value === 'string' || typeof value === 'number')
        && value !== ''
      ) {
        const encoded = encodeURIComponent(value);
        urlParameters.push(`${key}=${encoded}`);
      }
    }
  }

  return urlParameters.join('&');
}

/**
 * Represents a search response containing items and paginatiion, allows page navigation.
 *
 * @property {array} items Items on the current page
 * @property {object} info Object containing pagination information
 * @property {bool} items Items on the current page
 */
class SearchResponse {
  /**
   * @param {function} errorHandler If given, will be called if an exception occurs when
   *                                fetching or parsing response data. Returning a true
   *                                value will rethrow the error.
   */
  constructor(errorHandler) {
    this.errorHandler = errorHandler;
    this.requestPending = false;
    this.dataLoadedCallback = null;
    this.clear();
  }

  setDataLoadedCallbackSingle(callback) {
    this.dataLoadedCallback = callback;
  }

  cancelRequest() {
    this.requestPending = false;
  }

  // async
  updateRequest(requestData) {
    Object.assign(this.requestData, requestData);
    const urlParameters = BuildSearchUrlParameters(this.requestData);
    return this.requestUrlInternal(this.requestEndpoint, urlParameters);
  }

  // async
  requestUrlInternal(endpoint, urlParameters) {
    let requestedUrl = endpoint;
    let urlParametersWithPrefix = '';

    if (urlParameters) {
      urlParametersWithPrefix = `?${urlParameters}`;
      requestedUrl += urlParametersWithPrefix;
    }

    // For debugging and testing with alternative endpoints:
    const urlPrefix =      window.localStorage
      && window.localStorage.getItem('searchUseEndpointPrefix');
    if (urlPrefix && urlPrefix !== 'false') {
      requestedUrl = urlPrefix + requestedUrl;
    }

    // Update page URL
    if (window.history && window.history.replaceState) {
      const newLocation = `${
        window.location.pathname
      }${urlParametersWithPrefix}${window.location.hash}`;
      window.history.replaceState({}, 'Filtered results', newLocation);
    }

    // Ignore requests identical to the current one
    if (this.currentRequestUrl === requestedUrl) {
      return emptyPromise();
    }

    const requestUniqueId = Math.random();
    this.requestPending = requestUniqueId;

    // Request (Wrapped for error handling)
    this.currentRequestUrl = requestedUrl;
    return new Promise((resolve, reject) => {
      this.errorHandlerWrapper(() => {
        // Check if should use mock data
        let promise;
        if (shouldUseMockData()) {
          const { promise: promiseDestructed } = SearchMockData(requestedUrl);
          promise = promiseDestructed; // linter requires destruction
        } else {
          promise = axios.get(requestedUrl);
        }
        promise
          .then((response) => {
            // Check if it's a response to the pending request, if not, ignore
            if (this.requestPending === requestUniqueId) {
              this.setRawData(response.data);
            }
            resolve(this);
          })
          .catch((error) => {
            reject(error);
          });
      });
    });
  }

  generateQueryParameters(additionalParameters) {
    const params = {};
    this.facets.forEach((facet) => {
      facet.FacetResults.forEach((facetValue) => {
        if (facetValue.IsSelected) {
          const paramValue = params[facet.FacetAlias] || [];
          paramValue.push(facetValue.Udi);
          params[facet.FacetAlias] = paramValue;
        } else {
          params[facet.FacetAlias] = params[facet.FacetAlias] || [];
        }
      });
    });
    return Object.assign({}, params, additionalParameters);
  }

  clear() {
    this.requestData = {};
    this.requestEndpoint = null;
    this.currentRequestUrl = null;
    this.items = [];
    this.facets = [];
    this.info = this.info || {};
    Object.assign(this.info, {
      SearchQuery: null,
      Total: null,
      CurrentPage: null,
      PageSize: null,
      PageCount: null,
      HasNext: false,
      HasPrevious: false,
    });
    const pageNumberMatch = /pageNumber=([0-9]+)/.exec(window.location.search);
    if (pageNumberMatch) {
      this.info.CurrentPage = parseInt(pageNumberMatch[1], 10);
    }
  }

  setRawData(data) {
    if (!data.Data || !data.Pagination) {
      const message =        'SearchResponse: "Data" and "Pagination" keys required in response.';
      throw new Error(message);
    }

    this.requestEndpoint =      data.Endpoint || (data.Pagination.BaseUrl || '').replace(/\?.*$/, '');
    this.items.splice(0, this.items.length);
    (data.Data || []).forEach((item) => {
      this.items.push(item);
    });
    this.facets = (data.FacetResult && data.FacetResult.Facets) || [];

    this.info.SearchQuery = data.SearchQuery;
    const p = data.Pagination;
    this.info.Total = parseInt(p.Total, 10);
    this.info.CurrentPage = parseInt(p.CurrentPage, 10);
    this.info.PageSize = parseInt(p.PageSize, 10);
    this.info.PageCount = Math.ceil(this.info.Total / this.info.PageSize);
    this.info.ResultsPageUrl = p.ResultsPageUrl;
    this.info.SiteId = p.SiteId;

    // Update requestData object with most current data so the
    // context is preserved in continued requests.
    // this.requestData.query = data.SearchQuery;

    const facetParameters = {};

    if (data.FacetResult) {
      data.FacetResult.Facets.forEach((facet) => {
        facet.FacetResults.forEach((facetValue) => {
          if (facetValue.IsSelected) {
            const parameterArray = facetParameters[facet.FacetAlias] || [];
            parameterArray.push(facetValue.Udi);
            facetParameters[facet.FacetAlias] = parameterArray;
          }
        });
      });
    }

    if (data.VipProfiles) {
      facetParameters.vips = data.VipProfiles;
    }

    const initialSiteId = data.Pagination.SiteId;

    Object.assign(
      this.requestData,
      {
        // query: data.SearchQuery,
        pageNumber: data.Pagination.CurrentPage,
      },
      facetParameters,
    );

    if (typeof this.dataLoadedCallback === 'function') {
      this.dataLoadedCallback(this);
    }
  }

  errorHandlerWrapper(fn) {
    try {
      return fn();
    } catch (error) {
      if (this.errorHandler) {
        if (this.errorHandler(error)) {
          throw error;
        }
      }
      return null;
    }
  }
}

const searchStoreInstances = {};
export default function search(instanceName) {
  const key = instanceName || 'default';
  if (!Object.hasOwnProperty.call(searchStoreInstances, key)) {
    const instance = new SearchResponse();
    searchStoreInstances[key] = instance;

    if (key === 'default') {
      // Initialise with data from the server
      if (window.initialSearchResultsObjectData) {
        instance.setRawData(window.initialSearchResultsObjectData);
      } else if (shouldUseMockData()) {
        instance.setRawData(SearchMockData(window.location.href));
        window.searchMockDataGenerator = SearchMockData;
      }
    }
  }
  return searchStoreInstances[key];
}
